"use client";

import Image from "next/image";
import Link from "next/link";
import * as React from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  Bell,
  Download,
  Youtube,
  Info,
  Instagram,
  ListTree,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { trackLead } from "@/lib/metaPixel";
import { useIndexShell } from "@/components/article-shell/IndexShellContext";
import {
  MobileBottomToolRegistration,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";

interface PDFProduct {
  id: string;
  title: string;
  description: string;
  /** 좌측 네비 목록 항목용 간결 라벨 — 없으면 title fallback. */
  navLabel?: string;
  price: number;
  discountedPrice?: number;
  image: string;
  href: string;
  badge?: string;
  isReservation?: boolean;
}

const products2026Round3: PDFProduct[] = [
  {
    id: "26-3-compressed-pdf",
    title: "2026년 3회 실기 이론 요약 PDF",
    navLabel: "이론 요약 PDF",
    description: "3회 학습 범위와 우선순위를 반영한 실기 이론 요약 PDF입니다.",
    price: 10000,
    discountedPrice: 8000,
    image: "/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp",
    href: "/summary-pdf/26-3-compressed",
    badge: "새로오픈",
  },
  {
    id: "26-3-coding-pdf",
    title: "2026년 3회 실기 코딩 요약 PDF",
    navLabel: "코딩 요약 PDF",
    description: "C·Java·Python·SQL 코드와 실행 흐름을 정리한 실기 코딩 요약 PDF입니다.",
    price: 10000,
    discountedPrice: 8000,
    image: "/summary-pdf/26-1-coding-compressed/coding_compressed_gamja.webp",
    href: "/summary-pdf/26-3-coding-compressed",
    badge: "새로오픈",
  },
];

const products2026: PDFProduct[] = [
  {
    id: "26-1-compressed-pdf",
    title: "2026년 1·2회 통합 실기 이론 요약 PDF",
    navLabel: "이론 요약 PDF",
    description: "2026년 1·2회 통합 압축 이론 요약 PDF입니다.",
    price: 10000,
    discountedPrice: 8000,
    image: "/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp",
    href: "/summary-pdf/26-1-compressed",
    badge: "업데이트 종료",
  },
  {
    id: "26-1-coding-pdf",
    title: "2026년 1·2회 통합 실기 코딩 요약 PDF",
    navLabel: "코딩 요약 PDF",
    description: "기출 코딩 문제를 기반으로 핵심 코드 패턴을 정리한 1·2회 통합 코딩 요약 PDF입니다.",
    price: 10000,
    discountedPrice: 8000,
    image: "/summary-pdf/26-1-coding-compressed/coding_compressed_gamja.webp",
    href: "/summary-pdf/26-1-coding-compressed",
    badge: "업데이트 종료",
  },
];

// 회차별 명시 배열 — YEAR_SECTIONS가 그대로 참조한다(id prefix 문자열 필터 미사용 → 새 상품 누락 footgun 방지).
const products2025Round3: PDFProduct[] = [
  {
    id: "25-3-super-compressed-pdf",
    title: "[초압축] 2025년 3회 실기 이론 요약 PDF",
    navLabel: "초압축 이론 요약 PDF",
    description: "출제 경향 분석으로 엄선한 이론 범위를 완벽 마무리",
    price: 8000,
    discountedPrice: 5000,
    image: "/main/3d_super_compressed_gamja.webp",
    href: "/summary-pdf/25-3-super-compressed",
    badge: "업데이트 종료",
  },
  {
    id: "25-3-compressed-pdf",
    title: "[압축] 2025년 3회 실기 이론 요약 PDF",
    navLabel: "압축 이론 요약 PDF",
    description: "7장으로 이론 범위 걱정 끝. 3회차 11문제 중 9문제 적중!",
    price: 10000,
    discountedPrice: 8000,
    image: "/main/3d_compress_gamja.webp",
    href: "/summary-pdf/25-3-compressed",
    badge: "업데이트 종료",
  },
];

const products2025Round2: PDFProduct[] = [
  {
    id: "25-2-super-compressed-pdf",
    title: "[초압축] 2025년 2회 실기 이론 요약 PDF",
    navLabel: "초압축 이론 요약 PDF",
    description: "2회차 11문제 중 7문제 적중. 핵심 개념만 담은 초압축 요약.",
    price: 1000,
    image: "/main/3d_super_compressed_gamja.webp",
    href: "/summary-pdf/25-2-super-compressed",
    badge: "업데이트 종료",
  },
];

/** 연도 안의 회차 그룹 — 같은 연도라도 회차(1회/2회/3회)별로 카드를 나눈다. */
interface RoundGroup {
  /** 회차 라벨 (서브섹션 소제목 "1회" 등) */
  round: string;
  products: PDFProduct[];
  /** 회차 상태 배지 (선택) — 예: "준비중". 준비중 회차는 상품·상세 링크·구매 CTA를 노출하지 않는다. */
  badge?: string;
}

interface YearSection {
  /** 연도 식별자 (예: "year-2026") — 좌측/인라인 셀렉터의 선택 상태 key */
  id: string;
  /** 연도 라벨 (h2 / 셀렉터 텍스트) */
  year: string;
  /** 상태 배지 (선택) */
  badge?: { label: string; tone: "new" | "ended" };
  /** 회차별 카드 그룹 (최신 회차 먼저) */
  rounds: RoundGroup[];
}

/**
 * 연도 섹션 SSOT — 좌측 네비 목록(CatalogNav)과 본문 카드 그리드가 같은 데이터를 소비한다.
 * 새 연도/회차/상품 추가 시 이 배열(+ navLabel)만 갱신하면 네비 목록·그리드가 함께 반영된다.
 *
 * 연도 → 회차(round) → 상품(products) 의 중첩 구조. 본문은 최신 연도(YEAR_SECTIONS[0]=2026)만
 * 회차별 서브섹션으로 나눠 렌더하고, 이전 연도/회차는 좌측 네비 목록에서 각 상세 페이지로 이동한다.
 */
const YEAR_SECTIONS: YearSection[] = [
  {
    id: "year-2026",
    year: "2026년",
    rounds: [
      { round: "3회", products: products2026Round3 },
      { round: "1·2회", products: products2026 },
    ],
  },
  {
    id: "year-2025",
    year: "2025년",
    rounds: [
      { round: "3회", products: products2025Round3 },
      { round: "2회", products: products2025Round2 },
    ],
  },
];

function PDFCard({ product }: { product: PDFProduct }) {
  const isReservation = product.isReservation;

  return (
    <div className="group relative flex flex-col rounded-xl border bg-card overflow-hidden transition-all hover:shadow-lg hover:border-amber-400/50">
      {/* 배지 */}
      {product.badge && (
        <span className={`absolute top-3 right-3 z-10 text-white text-xs font-medium px-2 py-1 rounded-full ${
          product.badge === "업데이트 종료" ? "bg-gray-500" :
          product.badge === "출시 예정" ? "bg-blue-500" :
          product.badge === "새로오픈" ? "bg-emerald-700 dark:bg-emerald-400 dark:text-emerald-950" : "bg-amber-500"
        }`}>
          {product.badge}
        </span>
      )}

      {/* 이미지 */}
      <div className="relative h-36 sm:h-auto sm:aspect-square bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/30 flex items-center justify-center p-3 sm:p-6">
        <Image
          src={product.image}
          alt={product.title}
          width={150}
          height={150}
          className="object-contain transition-transform group-hover:scale-105"
        />
      </div>

      {/* 정보 */}
      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-semibold text-base mb-2 line-clamp-2">{product.title}</h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
          {product.description}
        </p>

        {/* 가격 */}
        <div className="mb-4">
          {isReservation ? (
            <span className="text-sm text-muted-foreground">출시 예정</span>
          ) : (
            <div className="flex flex-col gap-0.5">
              {product.discountedPrice ? (
                <>
                  <span className="text-base font-bold">
                    {product.price.toLocaleString()}원
                  </span>
                  <span className="text-xs text-red-500 font-medium">
                    유튜브 구독시: {product.discountedPrice.toLocaleString()}원
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold">
                  {product.price.toLocaleString()}원
                </span>
              )}
            </div>
          )}
        </div>

        {/* 버튼 */}
        {isReservation ? (
          <Button asChild variant="outline" className="w-full gap-2">
            <Link href={product.href}>
              <Bell className="h-4 w-4" aria-hidden />
              출시 알림 받기
            </Link>
          </Button>
        ) : (
          <Button asChild className="w-full bg-amber-500 hover:bg-amber-600 text-white">
            <Link href={product.href}>자세히 보기</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

/** 연도 섹션 배지 — 새로오픈(green)/업데이트 종료(muted). 다크 페어 포함. */
function YearBadge({ badge }: { badge: NonNullable<YearSection["badge"]> }) {
  if (badge.tone === "new") {
    return (
      <span className="rounded-full border border-green-600 px-2 py-1 text-xs font-medium text-green-600 dark:border-green-400 dark:text-green-400">
        {badge.label}
      </span>
    );
  }
  return (
    <span className="rounded-full border border-border px-2 py-1 text-xs font-medium text-muted-foreground">
      {badge.label}
    </span>
  );
}

/**
 * 회차 상태 배지 — "준비중" 등. 다음 회차 오픈 대기 상태를 나타내는 coming-soon 톤(blue 아웃라인,
 * PDFCard "출시 예정"과 동일 계열). 좌측/인라인 카탈로그 회차 버튼과 중앙 회차 제목에서 공통 사용.
 */
function RoundBadge({ label }: { label: string }) {
  return (
    <span className="rounded-full border border-blue-500 px-2 py-0.5 text-[11px] font-medium text-blue-600 dark:border-blue-400 dark:text-blue-400">
      {label}
    </span>
  );
}

/**
 * 본문 중앙 — 좌측/인라인 트리의 선택 상태(selectedYearId + selectedRound)를 반영해
 * 그 연도(필요 시 그 회차만) 회차별 서브섹션 카드 그리드를 렌더(셸/폴백 공통).
 *
 * - `selectedRound === null` → 그 연도의 모든 회차(회차별 h3 서브섹션 + 카드 그리드)
 * - `selectedRound === "1회"` 등 → 그 연도에서 해당 회차만 필터
 *
 * 기본값(selectedYearId="year-2026", selectedRound=null)은 상수 초기 상태라 SSR·첫 렌더가
 * 모두 "2026 전체(1회)"로 동일 → hydration mismatch 없음. 좌측 슬롯 portal 여부와 무관하게
 * 같은 selected* props 를 받으므로 셀렉터가 어디 있든 같은 중앙을 필터한다.
 */
function YearSections({
  selectedYearId,
  selectedRound,
}: {
  selectedYearId: string;
  selectedRound: string | null;
}) {
  const section =
    YEAR_SECTIONS.find((s) => s.id === selectedYearId) ?? YEAR_SECTIONS[0];
  const rounds = selectedRound
    ? section.rounds.filter((r) => r.round === selectedRound)
    : section.rounds;

  return (
    <section className="mb-12">
      <div className="mb-6 flex items-center gap-3">
        <h2 className="text-2xl font-bold">{section.year}</h2>
        {section.badge && <YearBadge badge={section.badge} />}
      </div>
      <div className="flex flex-col gap-8">
        {rounds.map((group) => (
          <div key={group.round}>
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {section.year} {group.round}
              </h3>
              {group.badge && <RoundBadge label={group.badge} />}
            </div>
            {group.products.length > 0 ? (
              <div className="grid grid-cols-2 gap-6 lg:grid-cols-3">
                {group.products.map((product) => (
                  <PDFCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-muted/40 p-8 text-center text-sm text-muted-foreground">
                해당 회차 요약 PDF는 준비중입니다. 오픈되면 이곳에서 안내드릴게요.
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * 연도/회차 필터 버튼의 active/비active 상태 클래스(CatalogNav 연도·회차 버튼 공용).
 * active = 브랜드 amber 강조(dark 페어 포함), 비active = 토큰(text-foreground/hover:bg-accent).
 */
function filterButtonStateClass(isActive: boolean): string {
  return isActive
    ? "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
    : "border-transparent text-foreground hover:bg-accent";
}

/** 카탈로그 트리 제목 SSOT — 좌측/인라인 heading 과 모바일 하단 dock 시트 title 공용. */
const CATALOG_NAV_TITLE = "요약 PDF 둘러보기";

/**
 * 카탈로그 트리의 세 렌더 위치(좌측 슬롯 portal · 본문 인라인 · 하단 dock 시트)가 공유하는
 * 최상위 필터 상태 props. SummaryPDFClient 가 소유하고 그대로 내려준다.
 */
interface CatalogFilterProps {
  selectedYearId: string;
  selectedRound: string | null;
  /** 연도 버튼 클릭 — 그 연도 전체(회차별)로 필터(selectedRound=null). */
  onSelectYear: (yearId: string) => void;
  /** 회차 버튼 클릭 — 그 연도의 해당 회차만 필터. */
  onSelectRound: (yearId: string, round: string) => void;
}

/** CatalogNav 전용 props — dock 도구는 hideHeading 을 직접 고정하므로 받지 않는다. */
type CatalogNavProps = CatalogFilterProps & {
  /** 하단 dock 시트처럼 바깥이 이미 같은 제목을 보여줄 때 내부 h2 를 생략한다(중복 제목 방지). */
  hideHeading?: boolean;
};

/**
 * 좌측 통합 카탈로그 네비 — **3단계 항상 펼쳐진(always-open) 트리**(JCG-2120 후속 디자인).
 *
 * 위계: 연도 → 회차 → PDF. 들여쓰기로 3단계를 시각화하며 토글/접기/팝오버 없이 모두 상시 노출한다.
 *  - **연도** = 중앙을 그 연도 전체(회차별)로 필터하는 `<button aria-pressed>`(상세 이동 아님).
 *  - **회차** = 중앙을 그 연도의 해당 회차만으로 필터하는 `<button aria-pressed>`(상세 이동 아님).
 *  - **PDF** = 상세 페이지로 이동하는 `<Link href={product.href}>`(navLabel ?? title, 끝에 ArrowRight).
 *
 * 상태(selectedYearId/selectedRound)는 SummaryPDFClient 최상위가 소유하고 props 로 주입되므로,
 * 좌측 슬롯 portal(데스크탑)과 모바일 인라인 폴백이 같은 컴포넌트로 동일 상태를 공유한다 —
 * 셀렉터가 어디 있든 같은 중앙을 필터한다.
 *
 * - 토글 상태가 없어 SSR HTML 에 트리가 항상 포함된다(SEO/no-JS).
 * - active(연도/회차 버튼) 스타일은 브랜드 amber 강조(`border-amber-500` + `bg-amber-50
 *   dark:bg-amber-950/40` + `text-amber-700 dark:text-amber-300`, dark 페어 — dark-mode-colors.md §2).
 *   비active 버튼은 토큰(`text-foreground`/`hover:bg-accent`), PDF 링크는 `text-muted-foreground
 *   hover:text-foreground hover:bg-accent` → light/dark 자동 대응(dark-mode-colors.md §1).
 * - 모든 인터랙티브 요소에 `focus-visible:ring-2 focus-visible:ring-amber-500`(amber 브랜드,
 *   light/dark 공통 — dark-mode-colors.md §2). `<nav aria-label>` + heading 으로 스크린리더 맥락 제공.
 */
function CatalogNav({
  selectedYearId,
  selectedRound,
  onSelectYear,
  onSelectRound,
  hideHeading,
}: CatalogNavProps) {
  return (
    <nav
      aria-label="연도·회차별 요약 PDF 보기"
      className="flex flex-col gap-4"
    >
      {!hideHeading && (
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {CATALOG_NAV_TITLE}
        </h2>
      )}
      <ul className="flex flex-col gap-3">
        {YEAR_SECTIONS.map((section) => {
          const yearActive =
            selectedYearId === section.id && selectedRound === null;
          return (
            <li key={section.id} className="flex flex-col gap-1">
              {/* 1단계: 연도 — 그 연도 전체(회차별)로 필터 */}
              <button
                type="button"
                aria-pressed={yearActive}
                onClick={() => onSelectYear(section.id)}
                className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${filterButtonStateClass(
                  yearActive,
                )}`}
              >
                <span className="truncate">{section.year}</span>
                {section.badge && <YearBadge badge={section.badge} />}
              </button>

              {/* 2단계: 회차들 — 들여써서 그 연도의 해당 회차만 필터 */}
              <ul className="flex flex-col gap-1 pl-3">
                {section.rounds.map((group) => {
                  const roundActive =
                    selectedYearId === section.id &&
                    selectedRound === group.round;
                  return (
                    <li key={group.round} className="flex flex-col">
                      <button
                        type="button"
                        aria-pressed={roundActive}
                        onClick={() => onSelectRound(section.id, group.round)}
                        className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${filterButtonStateClass(
                          roundActive,
                        )}`}
                      >
                        <span>{group.round}</span>
                        {group.badge && <RoundBadge label={group.badge} />}
                      </button>

                      {/* 3단계: PDF들 — 더 들여써서 각 상세 페이지로 이동(Link) */}
                      <ul className="flex flex-col pl-3">
                        {group.products.map((product) => (
                          <li key={product.id}>
                            <Link
                              href={product.href}
                              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                            >
                              <span className="truncate">
                                {product.navLabel ?? product.title}
                              </span>
                              <ArrowRight
                                className="h-3.5 w-3.5 shrink-0 opacity-60"
                                aria-hidden
                              />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** 구매 안내·특전 항목 SSOT — 우측 사이드바 카드(PurchaseNotesAside)와 하단 dock `안내사항` 시트 공통. */
const PURCHASE_NOTES = [
  {
    icon: Download,
    text: "구매한 회차·상품의 PDF를 구매 목록에서 다운로드할 수 있습니다.",
  },
  {
    icon: Youtube,
    text: "유튜브 ‘정처기 감자’ 채널 구독 시 할인가가 적용됩니다.",
  },
  {
    icon: Instagram,
    text: "PDF 관련 문의: 정처기 감자 인스타그램 DM",
  },
] as const;

/**
 * 안내·특전 항목 목록(아이콘 + 설명) — 카드 chrome 없이 목록만 그린다.
 * 우측 사이드바 카드와 모바일 하단 dock 시트가 공유한다.
 *
 * `dense` 는 폭이 좁은 우측 사이드바 카드용 축약 크기다. 시트는 본문 폭을 다 쓰므로 기본(text-sm)
 * 으로 두어, 이 시트가 대체하는 본문 하단 인라인 박스와 같은 가독성을 유지한다.
 */
function PurchaseNotesList({ dense = false }: { dense?: boolean }) {
  return (
    <ul className="flex flex-col gap-3">
      {PURCHASE_NOTES.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-start gap-2">
          <Icon
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400"
            aria-hidden
          />
          <span
            className={`leading-relaxed text-muted-foreground ${
              dense ? "text-xs" : "text-sm"
            }`}
          >
            {text}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 구매 안내·특전 요약 컴팩트 카드 — 셸 우측 사이드바(슬롯 준비 후)와 셸 SSR/첫 렌더 구간의 본문
 * 하단 인라인이 **같은 컴포넌트**를 쓴다(ShellAside 가 두 경로에 같은 children 을 흘린다).
 * 클래식(<1024px)에서는 하단 dock `안내사항` 시트가 목록만 보여주므로 이 카드는 렌더되지 않는다.
 */
function PurchaseNotesAside() {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        구매 안내 · 특전
      </h2>
      <PurchaseNotesList dense />
    </div>
  );
}

/**
 * Complete 이용권 안내 카드 — 우측 사이드바 특전(셸) + 폴백(모바일) 본문 하단 공통.
 *
 * 사실(src/types/pass.ts): Complete = 요약 PDF(이론·코딩) 포함 + Premium 전 기능 포함,
 * 30,000원 / 유튜브 구독 시 25,000원. CTA → /membership.
 *
 * 톤 선택: 페이지 테마(amber)로 통일. 레퍼런스 배너(26-1-compressed)가 amber→orange
 * 그라데이션이라 그 정체성을 잇되, 그라데이션은 dark variant를 못 받으므로(dark-mode-colors.md
 * §4-5) raw amber 토큰 + dark 페어로 작성한다. Complete 브랜드 blue 대신 amber를 고른 이유는
 * 이 카드가 페이지(amber 테마)의 보조 안내라서 — card-variants.md §1(complete=blue)는
 * 이용권 비교 카드(PassCard)에 한정된 규칙이고, 여기선 페이지 일관성을 우선한다.
 */
function CompletePassCard() {
  return (
    <Link
      href="/membership"
      className="group block rounded-xl border border-amber-300 bg-amber-50 p-4 transition-colors hover:border-amber-400 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-800 dark:bg-amber-950/40 dark:hover:bg-amber-950/60"
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <Sparkles
          className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400"
          aria-hidden
        />
        <h2 className="text-sm font-semibold text-amber-700 dark:text-amber-300">
          Complete 이용권
        </h2>
      </div>
      <ul className="mb-3 flex flex-col gap-1 text-xs leading-relaxed text-amber-700 dark:text-amber-200">
        <li>• 요약 PDF(이론·코딩) 포함</li>
        <li>• Premium 전 기능 포함</li>
      </ul>
      <p className="mb-3 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">30,000원</span> · 유튜브
        구독 시{" "}
        <span className="font-semibold text-foreground">25,000원</span>
      </p>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
        이용권 보러가기
        <ArrowRight
          className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </span>
    </Link>
  );
}

/**
 * 클래식(<1024px) 하단 dock 도구 (JCG-3616).
 *
 * 데스크탑 셸은 좌측 사이드바에 카탈로그 트리를, 우측 사이드바에 구매 안내·Complete 이용권 카드를
 * 둔다. 클래식 구간엔 사이드바가 없어 예전엔 트리를 본문 상단에, 안내·이용권 카드를 본문 하단에
 * 쌓았다(트리는 세로로 길어 카드 그리드를 첫 화면 밖으로 밀어냈다). 셋을 모두 하단 dock 도구로
 * 옮겨 첫 화면을 카드 그리드에 내주고, 스크롤 없이도 안내·특가에 닿게 한다.
 * `CommunityBoardMobileDock` 이 사이드바 카드 스택을 dock 으로 대체한 것과 같은 방식이다.
 *
 * `MobileBottomToolProvider` 는 LayoutSwitch 의 클래식 분기에만 있으므로 호출부가 `!inShell` 로
 * 게이트한다. 시트를 닫는 동작은 공통 `closeOnContentInteraction` 이 버블 단계에서 처리하므로
 * 이 컴포넌트는 dock context 를 구독하지 않는다 — 덕분에 등록(updateTool) → context 변경 →
 * 재렌더 경로가 없어 `tool` 객체를 매 렌더 새로 만들어도 안전하다(PassRateMobileTools 와 동일 형태).
 *
 * 다만 안전하다고 공짜는 아니라, **필터 상태를 쓰지 않는 두 도구는 모듈 상수로 올린다**. 그래야
 * 연도·회차를 바꿀 때 재등록(updateTool → provider 재렌더)이 3회가 아니라 카탈로그 1회로 끝난다.
 * 동시에 "3개 중 카탈로그만 props 에 의존한다"는 사실이 코드에 드러난다.
 */
const SUMMARY_PDF_NOTES_TOOL: MobileBottomToolItem = {
  id: "summary-pdf-notes",
  kind: "panel",
  label: "안내사항",
  icon: Info,
  order: 1,
  ariaLabel: "안내사항, 구매 안내와 특전",
  sheet: {
    title: "구매 안내 · 특전",
    // 3항목뿐이라 콘텐츠 높이만큼만(hug) 쓰고, 목차·그래프처럼 dock 에 붙여 렌더한다.
    resizable: false,
    attached: true,
    content: <PurchaseNotesList />,
  },
};

const SUMMARY_PDF_PASS_TOOL: MobileBottomToolItem = {
  id: "summary-pdf-pass",
  kind: "panel",
  label: "특가",
  icon: Sparkles,
  order: 2,
  // 프로모션 강조 — tone 은 `amber | emerald` 둘뿐이고(mobile-bottom-tools/types.ts),
  // emerald 는 resting 상태부터 채워진 칩이라 투명 배경인 amber 2개(둘러보기·안내사항) 옆에서
  // 유일하게 튄다. 강조가 희석되지 않게 dock 당 하나로 제한한다(mobile-bottom-tools.md §2).
  // 색 의미: 이 페이지에서 emerald 는 `새로오픈` 배지(PDFCard)와 같은 계열이고, 할인가 red
  // (`유튜브 구독시: N원`)·브랜드 amber 와는 구분된다. 출석 도구가 쓰던 emerald 를 dock 강조로
  // 넓혀 쓰는 것이므로 남용하면 두 의미가 섞인다. 시트가 여는 카드는 amber 를 유지해
  // 페이지 톤(CompletePassCard 주석의 complete=blue 대신 amber 선택)을 깨지 않는다.
  tone: "emerald",
  ariaLabel: "특가, Complete 이용권 할인 안내",
  sheet: {
    title: "특가 안내",
    resizable: false,
    attached: true,
    // 카드 전체가 /membership 링크라, 누르면 이동과 함께 시트를 닫는다.
    closeOnContentInteraction: true,
    content: <CompletePassCard />,
  },
};

function SummaryPdfMobileTools(props: CatalogFilterProps) {
  return (
    <>
      <MobileBottomToolRegistration
        tool={{
          id: "summary-pdf-catalog",
          kind: "panel",
          label: "둘러보기",
          icon: ListTree,
          order: 0,
          // 보이는 라벨을 부분 문자열로 포함해야 음성 제어가 동작한다(WCAG 2.5.3 Label in Name).
          ariaLabel: "둘러보기, 연도·회차별 요약 PDF 목록",
          sheet: {
            title: CATALOG_NAV_TITLE,
            // 3단계라 콘텐츠가 길다 → 지도 패널과 같은 resizable 0.5 로 열고 내부 스크롤 + 핸들로 조절.
            resizable: true,
            defaultHeightRatio: 0.5,
            // 연도·회차를 고르면 결과(카드 그리드)가 시트 뒤에서 바뀌고, PDF 링크도 같은 세그먼트
            // 이동이라 등록 컴포넌트가 살아 있어 시트가 자동으로 닫히지 않는다.
            closeOnContentInteraction: true,
            content: <CatalogNav hideHeading {...props} />,
          },
        }}
      />
      <MobileBottomToolRegistration tool={SUMMARY_PDF_NOTES_TOOL} />
      <MobileBottomToolRegistration tool={SUMMARY_PDF_PASS_TOOL} />
    </>
  );
}

/**
 * 셸 사이드바 조각 렌더러 — 슬롯이 준비되면 그 슬롯으로 portal, 아직이면(SSR/첫 렌더) 같은 내용을
 * 본문 흐름에 인라인으로 렌더한다.
 *
 * "두 경로의 내용은 언제나 같다"는 불변식을 이 컴포넌트가 소유하는 게 요점이다. 예전엔 조각마다
 * `slot ? portal : inline` 을 손으로 반복해서, 새 조각을 추가하는 사람이 한쪽 경로를 빠뜨리거나
 * (클래식에서 아무 데도 안 나옴) 게이트를 `!(inShell && slot)` 으로 잘못 써서 dock 과 이중 노출되는
 * 함정이 있었다. 클래식(<1024px)은 호출부가 `inShell` 로 걸러 하단 dock 에 맡기므로 여기로 오지 않는다.
 *
 * `inlineClassName` 은 본문 흐름에서만 필요한 여백(예: 아래 카드 그리드와의 간격)이라 portal 경로엔
 * 적용하지 않는다. 사이드바 안에서의 배치는 children 쪽에 둔다.
 */
function ShellAside({
  slot,
  inlineClassName,
  children,
}: {
  slot: HTMLElement | null;
  inlineClassName?: string;
  children: React.ReactNode;
}) {
  if (slot) return createPortal(children, slot);
  return inlineClassName ? (
    <div className={inlineClassName}>{children}</div>
  ) : (
    <>{children}</>
  );
}

const PAGE_TITLE = "요약 PDF";
const PAGE_SUBTITLE = "정처기 실기 이론·코딩 요약 PDF를 회차별로 확인하세요";

export default function SummaryPDFClient() {
  const { inShell, headerSlot, leftSlot, rightSlot } = useIndexShell();

  // 좌측 트리 ↔ 중앙 본문 공유 필터 상태(최상위 소유).
  // 상수 초기값(="year-2026", null)이라 SSR/첫 렌더가 모두 "2026 전체(1회)"로 동일 → hydration mismatch 없음.
  const [selectedYearId, setSelectedYearId] = React.useState(
    YEAR_SECTIONS[0].id,
  );
  const [selectedRound, setSelectedRound] = React.useState<string | null>(null);
  const gridRef = React.useRef<HTMLDivElement>(null);

  /**
   * 필터를 바꾸면 카드 그리드 상단으로 되돌린다. 선택에 따라 그리드 높이가 크게 달라져서
   * (연도 전체 4장 ↔ 한 회차 2장), 아래쪽까지 스크롤한 상태로 필터하면 문서가 짧아지며 브라우저가
   * 스크롤을 클램프해 방금 고른 카드가 아니라 푸터 근처를 보게 된다.
   *
   * rAF 로 미루는 이유: 모바일에서 이 조작은 하단 dock 시트 안에서 일어나고, 시트가 닫히며
   * `useBodyScrollLock` 이 **시트를 열 때의** 스크롤 위치를 `window.scrollTo` 로 복원한다.
   * 클릭 핸들러에서 바로 스크롤하면 그 복원에 덮어써지므로 다음 프레임에 측정·이동한다.
   */
  const scrollGridIntoView = React.useCallback(() => {
    requestAnimationFrame(() => {
      const el = gridRef.current;
      // 그리드 상단이 이미 화면 안이면(주로 페이지 최상단) 그대로 둔다 — 제목을 밀어내지 않는다.
      if (!el || el.getBoundingClientRect().top >= 0) return;
      el.scrollIntoView({ block: "start" });
    });
  }, []);

  // 연도 버튼 → 그 연도 전체(회차별), 회차 버튼 → 그 연도의 해당 회차만.
  const handleSelectYear = React.useCallback(
    (yearId: string) => {
      setSelectedYearId(yearId);
      setSelectedRound(null);
      scrollGridIntoView();
    },
    [scrollGridIntoView],
  );
  const handleSelectRound = React.useCallback(
    (yearId: string, round: string) => {
      setSelectedYearId(yearId);
      setSelectedRound(round);
      scrollGridIntoView();
    },
    [scrollGridIntoView],
  );

  // 세 렌더 위치가 공유하는 필터 props 1벌 — 어디서 눌러도 같은 중앙을 필터한다.
  const catalogProps: CatalogFilterProps = {
    selectedYearId,
    selectedRound,
    onSelectYear: handleSelectYear,
    onSelectRound: handleSelectRound,
  };

  // Meta Pixel Lead 이벤트 전송
  useEffect(() => {
    trackLead({
      content_name: "summary-pdf",
      content_category: "PDF",
    });
  }, []);

  return (
    <div className="px-4 py-8">
      {/* 데스크탑 셸 — 타이틀+부제를 상단 고정 바(headerSlot)에 portal */}
      {inShell &&
        headerSlot &&
        createPortal(
          <div className="mx-auto flex w-full max-w-[820px] flex-col items-center px-4 py-3 sm:px-8">
            <h1 className="text-xl font-bold">{PAGE_TITLE}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {PAGE_SUBTITLE}
            </p>
          </div>,
          headerSlot,
        )}

      {/* 본문 상단 타이틀 — 셸+슬롯 준비 시 상단 고정 바로 이동, 그 전엔(폴백) 본문 상단 중앙 정렬 */}
      {!(inShell && headerSlot) && (
        <div className="mb-12 text-center">
          <h1 className="mb-2 text-3xl font-bold">{PAGE_TITLE}</h1>
          <p className="text-muted-foreground">{PAGE_SUBTITLE}</p>
        </div>
      )}

      {/* 클래식(<1024px) — 좌측 트리와 우측 안내·이용권 카드를 모두 하단 dock 도구로 노출 (JCG-3616).
          MobileBottomToolProvider 가 LayoutSwitch 의 클래식 분기에만 있으므로 여기서만 등록한다.
          아래 셸 전용 분기들과 상호 배타라 같은 내용이 두 번 나오지 않는다. */}
      {!inShell && <SummaryPdfMobileTools {...catalogProps} />}

      {/* 셸 전용 카탈로그 트리 — 좌측 사이드바 슬롯, 준비 전이면 본문 상단 인라인(ShellAside).
          SSR 은 항상 셸(데스크탑 스냅샷)이라 인라인 경로가 HTML 에 남아 no-JS/SEO 가 유지된다. */}
      {inShell && (
        <ShellAside slot={leftSlot} inlineClassName="mb-8">
          <CatalogNav {...catalogProps} />
        </ShellAside>
      )}

      {/* 가운데(children) — 선택된 연도(필요 시 회차)만 회차별로 분할한 카드 그리드(셸/폴백 공통).
          scroll-mt 는 셸 상단 고정 바·모바일 Navbar 아래로 제목이 가리지 않게 하는 여유다. */}
      <div ref={gridRef} className="scroll-mt-20">
        <YearSections
          selectedYearId={selectedYearId}
          selectedRound={selectedRound}
        />
      </div>

      {/* 셸 전용 안내사항 + Complete 카드 — 우측 사이드바 슬롯, 준비 전이면 본문 하단 인라인
          (ShellAside). 클래식에선 위 dock 의 `안내사항`·`특가` 시트가 대신한다. */}
      {inShell && (
        <ShellAside slot={rightSlot}>
          <div className="flex flex-col gap-4">
            <PurchaseNotesAside />
            <CompletePassCard />
          </div>
        </ShellAside>
      )}
    </div>
  );
}
